import React, { useState, useEffect } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { usePermissions } from '../../hooks/usePermissions';
import PageHelp from '../../components/PageHelp';

interface FinancialAnalyticsData {
  overview: {
    totalRevenue: string;
    totalExpenses: string;
    netIncome: string;
    profitMargin: number;
    cashInflow: string;
    cashOutflow: string;
    netCashFlow: string;
    accountsReceivable: string;
    accountsPayable: string;
  };
  periods: {
    today: PeriodMetrics;
    thisWeek: PeriodMetrics;
    thisMonth: PeriodMetrics;
    thisYear: PeriodMetrics;
    lastMonth: PeriodMetrics;
    lastYear: PeriodMetrics;
  };
  trends: {
    revenue: TrendData[];
    expenses: TrendData[];
    profit: TrendData[];
  };
  receivables: {
    totalOutstanding: string;
    overdue: string;
    aging: {
      current: string;
      days31to60: string;
      days61to90: string;
      over90: string;
    };
  };
  payables: {
    totalOutstanding: string;
    overdue: string;
    aging: {
      current: string;
      days31to60: string;
      days61to90: string;
      over90: string;
    };
  };
  recentTransactions: Array<{
    date: string;
    type: 'INVOICE' | 'EXPENSE' | 'PAYMENT' | 'PURCHASE_INVOICE';
    description: string;
    amount: string;
    status: string;
  }>;
}

interface PeriodMetrics {
  revenue: string;
  expenses: string;
  profit: string;
  profitMargin: number;
  invoiceCount: number;
  expenseCount: number;
}

interface TrendData {
  period: string;
  value: string;
}

const FinancialAnalytics: React.FC = () => {
  const { user } = useAuthStore();
  const { hasPermission, loading: permissionsLoading } = usePermissions(user);
  const [analytics, setAnalytics] = useState<FinancialAnalyticsData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPeriod, setSelectedPeriod] = useState<'today' | 'thisWeek' | 'thisMonth' | 'thisYear' | 'lastMonth' | 'lastYear'>('thisMonth');

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '£0.00';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(num);
  };

  const formatPercentage = (value: number) => {
    if (isNaN(value)) return '0.00%';
    return `${value.toFixed(2)}%`;
  };

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get('/api/erp/analytics/dashboard', {
        params: {
          includeTrends: true,
          trendPeriod: 'monthly',
          trendMonths: 12,
        },
      });
      if (response.data.success) {
        setAnalytics(response.data.data);
      } else {
        toast.error(response.data.error || 'Failed to load analytics');
      }
    } catch (error: any) {
      console.error('Error loading analytics:', error);
      toast.error(error.response?.data?.error || 'Failed to load analytics');
    } finally {
      setIsLoading(false);
    }
  };

  if (permissionsLoading) {
    return <div className="p-6">Loading...</div>;
  }

  if (!hasPermission('erp_financial_report', 'read')) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-800">You do not have permission to view financial analytics.</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <p className="text-gray-600">Loading analytics...</p>
        </div>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="p-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <p className="text-yellow-800">No analytics data available.</p>
        </div>
      </div>
    );
  }

  const selectedPeriodData = analytics.periods[selectedPeriod];

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex items-center">
          <h1 className="text-2xl font-bold text-gray-900">Financial Analytics Dashboard</h1>
          <PageHelp articleKey="erp_analytics" />
        </div>
        <p className="text-gray-600 mt-1">Real-time financial metrics and insights</p>
      </div>

      {/* Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Revenue (YTD)</p>
          <p className="text-2xl font-bold text-green-600">
            {formatCurrency(analytics.overview.totalRevenue)}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Profit Margin: {formatPercentage(analytics.overview.profitMargin)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Total Expenses (YTD)</p>
          <p className="text-2xl font-bold text-red-600">
            {formatCurrency(analytics.overview.totalExpenses)}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Net Income: {formatCurrency(analytics.overview.netIncome)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Net Cash Flow (YTD)</p>
          <p className="text-2xl font-bold text-blue-600">
            {formatCurrency(analytics.overview.netCashFlow)}
          </p>
          <p className="text-xs text-gray-500 mt-2">
            In: {formatCurrency(analytics.overview.cashInflow)} | Out: {formatCurrency(analytics.overview.cashOutflow)}
          </p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-sm text-gray-600 mb-1">Outstanding</p>
          <p className="text-lg font-semibold text-gray-900">
            AR: {formatCurrency(analytics.overview.accountsReceivable)}
          </p>
          <p className="text-lg font-semibold text-gray-900">
            AP: {formatCurrency(analytics.overview.accountsPayable)}
          </p>
        </div>
      </div>

      {/* Period Comparison */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold text-gray-900">Period Comparison</h2>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value as any)}
            className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="today">Today</option>
            <option value="thisWeek">This Week</option>
            <option value="thisMonth">This Month</option>
            <option value="thisYear">This Year</option>
            <option value="lastMonth">Last Month</option>
            <option value="lastYear">Last Year</option>
          </select>
        </div>
        {selectedPeriodData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Revenue</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(selectedPeriodData.revenue)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriodData.invoiceCount} invoices
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Expenses</p>
              <p className="text-xl font-bold text-red-600">
                {formatCurrency(selectedPeriodData.expenses)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                {selectedPeriodData.expenseCount} expenses
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-sm text-gray-600 mb-1">Profit</p>
              <p className={`text-xl font-bold ${parseFloat(selectedPeriodData.profit) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {formatCurrency(selectedPeriodData.profit)}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Margin: {formatPercentage(selectedPeriodData.profitMargin)}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Receivables and Payables */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Receivables */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounts Receivable</h2>
          <div className="mb-4">
            <p className="text-sm text-gray-600">Total Outstanding</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(analytics.receivables.totalOutstanding)}
            </p>
          </div>
          <div className="mb-4">
            <p className="text-sm text-red-600">Overdue</p>
            <p className="text-xl font-semibold text-red-600">
              {formatCurrency(analytics.receivables.overdue)}
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Aging Analysis</p>
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current (0-30 days)</span>
                <span className="font-semibold">{formatCurrency(analytics.receivables.aging.current)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">31-60 days</span>
                <span className="font-semibold">{formatCurrency(analytics.receivables.aging.days31to60)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">61-90 days</span>
                <span className="font-semibold">{formatCurrency(analytics.receivables.aging.days61to90)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Over 90 days</span>
                <span className="font-semibold text-red-600">{formatCurrency(analytics.receivables.aging.over90)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Payables */}
        <div className="bg-white rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Accounts Payable</h2>
          <div className="mb-4">
            <p className="text-sm text-gray-600">Total Outstanding</p>
            <p className="text-2xl font-bold text-gray-900">
              {formatCurrency(analytics.payables.totalOutstanding)}
            </p>
          </div>
          <div className="mb-4">
            <p className="text-sm text-red-600">Overdue</p>
            <p className="text-xl font-semibold text-red-600">
              {formatCurrency(analytics.payables.overdue)}
            </p>
          </div>
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Aging Analysis</p>
            <div className="text-sm">
              <div className="flex justify-between">
                <span className="text-gray-600">Current (0-30 days)</span>
                <span className="font-semibold">{formatCurrency(analytics.payables.aging.current)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">31-60 days</span>
                <span className="font-semibold">{formatCurrency(analytics.payables.aging.days31to60)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">61-90 days</span>
                <span className="font-semibold">{formatCurrency(analytics.payables.aging.days61to90)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Over 90 days</span>
                <span className="font-semibold text-red-600">{formatCurrency(analytics.payables.aging.over90)}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Trends Chart */}
      {analytics.trends.revenue.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Revenue & Expenses Trends (Last 12 Months)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Period</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Revenue</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Expenses</th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Profit</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {analytics.trends.revenue.map((trend, index) => (
                  <tr key={index} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm text-gray-900">{trend.period}</td>
                    <td className="px-4 py-3 text-sm text-green-600 text-right font-semibold">
                      {formatCurrency(trend.value)}
                    </td>
                    <td className="px-4 py-3 text-sm text-red-600 text-right font-semibold">
                      {formatCurrency(analytics.trends.expenses[index]?.value || '0')}
                    </td>
                    <td className={`px-4 py-3 text-sm text-right font-semibold ${
                      parseFloat(analytics.trends.profit[index]?.value || '0') >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatCurrency(analytics.trends.profit[index]?.value || '0')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Transactions */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Recent Transactions</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {analytics.recentTransactions.map((trans, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {new Date(trans.date).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 rounded text-xs font-medium ${
                      trans.type === 'INVOICE' ? 'bg-green-100 text-green-800' :
                      trans.type === 'EXPENSE' || trans.type === 'PURCHASE_INVOICE' ? 'bg-red-100 text-red-800' :
                      'bg-blue-100 text-blue-800'
                    }`}>
                      {trans.type.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{trans.description}</td>
                  <td className={`px-4 py-3 text-sm text-right font-semibold ${
                    trans.type === 'INVOICE' || trans.type === 'PAYMENT' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {trans.type === 'INVOICE' || trans.type === 'PAYMENT' ? '+' : '-'}
                    {formatCurrency(trans.amount)}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{trans.status}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FinancialAnalytics;
